import { useEffect, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const MD_COMPONENTS = {
  h1: ({ node, ...props }) => <h2 className="font-display text-2xl font-semibold mt-8" {...props} />,
  h2: ({ node, ...props }) => <h2 className="font-display text-2xl font-semibold mt-8" {...props} />,
  h3: ({ node, ...props }) => <h3 className="font-display text-xl font-semibold mt-6" {...props} />,
  p: ({ node, ...props }) => <p className="text-[17px] leading-relaxed my-4" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc pl-6 space-y-2 text-[17px]" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-6 space-y-2 text-[17px]" {...props} />,
  a: ({ node, ...props }) => <a className="text-amber-600 hover:text-amber-700 underline underline-offset-2" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold text-stone-900" {...props} />,
  blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-amber-300 pl-4 italic text-stone-600" {...props} />,
  code: ({ node, ...props }) => <code className="bg-stone-100 rounded px-1.5 py-0.5 text-sm font-mono" {...props} />,
};

const EMPTY = {
  title: "",
  slug: "",
  description: "",
  hero: "",
  tags: "",
  read_mins: 5,
  body_markdown: "## Section heading\n\nWrite your post in Markdown. Use `##` for section headers and blank lines for paragraphs.",
  date: new Date().toISOString().slice(0, 10),
  published: false,
};

export default function AdminBlog() {
  const [posts, setPosts] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = new, otherwise post id
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/blog/admin/posts`);
      setPosts(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to load posts";
      if (e?.response?.status === 403) {
        toast.error("Admin access required");
      } else {
        toast.error(msg);
      }
      setPosts([]);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      title: p.title || "",
      slug: p.slug || "",
      description: p.description || "",
      hero: p.hero || "",
      tags: (p.tags || []).join(", "),
      read_mins: p.read_mins || 5,
      body_markdown: p.body_markdown || "",
      date: p.date || new Date().toISOString().slice(0, 10),
      published: !!p.published,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        slug: form.slug.trim() || undefined,
        description: form.description.trim(),
        hero: form.hero.trim(),
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        read_mins: Number(form.read_mins) || 5,
        body_markdown: form.body_markdown,
        date: form.date,
        published: form.published,
      };
      if (editing) {
        await axios.patch(`${API}/blog/admin/posts/${editing}`, payload);
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/blog/admin/posts`, payload);
        toast.success("Post created");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (p) => {
    try {
      await axios.patch(`${API}/blog/admin/posts/${p.id}`, { published: !p.published });
      toast.success(p.published ? "Moved to drafts" : "Published");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const onDelete = async (p) => {
    // Soft-delete + 10s undo pattern (server-side hard delete via cached snapshot on undo re-create)
    const snapshot = { ...p };
    // Optimistically remove
    setPosts((prev) => prev.filter((x) => x.id !== p.id));
    let undone = false;

    const finalize = async () => {
      if (undone) return;
      try {
        await axios.delete(`${API}/blog/admin/posts/${p.id}`);
      } catch (e) {
        toast.error("Delete failed on server — reloading");
        load();
      }
    };

    toast(`"${snapshot.title.slice(0, 40)}${snapshot.title.length > 40 ? "…" : ""}" removed`, {
      duration: 10000,
      action: {
        label: "Undo",
        onClick: async () => {
          undone = true;
          // Re-create by writing the same fields back
          try {
            await axios.post(`${API}/blog/admin/posts`, {
              title: snapshot.title,
              slug: snapshot.slug + "-restored",
              description: snapshot.description,
              hero: snapshot.hero,
              tags: snapshot.tags,
              read_mins: snapshot.read_mins,
              body_markdown: snapshot.body_markdown,
              date: snapshot.date,
              published: snapshot.published,
            });
            toast.success("Restored (new slug: -restored)");
          } catch {
            toast.error("Undo failed");
          }
          load();
        },
      },
    });

    // Fire actual delete after 10s
    setTimeout(finalize, 10000);
  };

  return (
    <div className="space-y-8" data-testid="admin-blog-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-amber-600 font-semibold">Admin</div>
          <h1 className="mt-1 font-display text-3xl font-bold">Blog</h1>
          <p className="mt-1 text-stone-600 text-sm">Write and publish articles that appear on your public `/blog` page.</p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="new-post-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New post
        </Button>
      </div>

      {posts === null ? (
        <div className="text-stone-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : posts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-stone-300 bg-white p-10 text-center text-stone-500" data-testid="empty-posts">
          No posts yet. Click <strong>New post</strong> to write your first one.
        </div>
      ) : (
        <div className="grid gap-4" data-testid="posts-list">
          {posts.map((p) => (
            <div key={p.id} className="bg-white border border-stone-200 rounded-2xl p-5 flex gap-5 items-start" data-testid={`post-row-${p.slug}`}>
              {p.hero ? (
                <img src={p.hero} alt="" className="w-28 h-20 rounded-xl object-cover flex-none" />
              ) : (
                <div className="w-28 h-20 rounded-xl bg-stone-100 flex-none" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 rounded-full border ${p.published ? "bg-emerald-50 text-emerald-800 border-emerald-100" : "bg-stone-100 text-stone-600 border-stone-200"}`}>
                    {p.published ? "Published" : "Draft"}
                  </span>
                  {p.tags?.slice(0, 3).map((t) => (
                    <span key={t} className="text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-100">{t}</span>
                  ))}
                </div>
                <h3 className="mt-2 font-display font-semibold text-lg leading-snug truncate">{p.title}</h3>
                <div className="mt-1 text-xs text-stone-500">
                  {p.date} · {p.read_mins} min · /blog/{p.slug}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-none">
                <Button variant="ghost" size="sm" onClick={() => togglePublish(p)} className="rounded-full" data-testid={`toggle-publish-${p.slug}`}>
                  {p.published ? <><EyeOff className="w-3.5 h-3.5 mr-1" />Unpublish</> : <><Eye className="w-3.5 h-3.5 mr-1" />Publish</>}
                </Button>
                {p.published && (
                  <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-stone-500 hover:text-stone-800 p-2 rounded-full hover:bg-stone-100" data-testid={`view-live-${p.slug}`}>
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                <Button variant="ghost" size="sm" onClick={() => openEdit(p)} className="rounded-full" data-testid={`edit-${p.slug}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onDelete(p)} className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-${p.slug}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="blog-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit post" : "New post"}</DialogTitle>
            <DialogDescription>Write in Markdown. Use the Preview tab to check how it will render on the public blog.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Preparing for your first Florida evaluation" data-testid="post-title-input" />
              </div>
              <div>
                <Label>Slug (URL)</Label>
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-generated from title" data-testid="post-slug-input" />
              </div>
            </div>

            <div>
              <Label>Description (SEO summary, 1–2 sentences)</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="post-description-input" />
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <Label>Hero image URL</Label>
                <Input value={form.hero} onChange={(e) => setForm({ ...form, hero: e.target.value })} placeholder="https://…" data-testid="post-hero-input" />
              </div>
              <div>
                <Label>Tags (comma-separated)</Label>
                <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="Compliance, Florida" data-testid="post-tags-input" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Read (mins)</Label>
                  <Input type="number" min="1" value={form.read_mins} onChange={(e) => setForm({ ...form, read_mins: e.target.value })} data-testid="post-readmins-input" />
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="post-date-input" />
                </div>
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Body</Label>
              <Tabs defaultValue="write" className="w-full">
                <TabsList>
                  <TabsTrigger value="write" data-testid="editor-write-tab">Write</TabsTrigger>
                  <TabsTrigger value="preview" data-testid="editor-preview-tab">Preview</TabsTrigger>
                </TabsList>
                <TabsContent value="write">
                  <Textarea
                    value={form.body_markdown}
                    onChange={(e) => setForm({ ...form, body_markdown: e.target.value })}
                    rows={18}
                    className="font-mono text-sm"
                    data-testid="post-body-input"
                  />
                  <p className="text-xs text-stone-500 mt-1.5">
                    Markdown: <code>## Section</code> for headings, <code>**bold**</code>, <code>[link](url)</code>, <code>-</code> for bullets.
                  </p>
                </TabsContent>
                <TabsContent value="preview">
                  <div className="max-w-none border rounded-xl p-5 bg-white min-h-[420px] text-stone-800" data-testid="post-preview-pane">
                    <ReactMarkdown components={MD_COMPONENTS}>{form.body_markdown || "*(nothing to preview)*"}</ReactMarkdown>
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            <div className="flex items-center justify-between bg-stone-50 border border-stone-200 rounded-xl px-4 py-3">
              <div>
                <div className="font-medium text-sm">Publish this post</div>
                <div className="text-xs text-stone-500">When on, it appears on `/blog` and its slug URL immediately.</div>
              </div>
              <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="post-published-switch" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full" data-testid="editor-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="editor-save">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : (editing ? "Save changes" : "Create post")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
